import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { store } from '../../config/firebase';
import { useColorMode } from '../../hooks/colorModeProvider';
import { GameStatus } from '../../constants/gameConstants';
import { liveGameDocs } from '../../helpers/games';
import PoweredBy from '../ui/poweredBy';
import BSCircularProgress from '../ui/progress/circularProgress';

const normalizeName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const getGroupList = (event) => {
  const groups = Array.isArray(event?.teamGroups) ? event.teamGroups : [];
  return groups.filter((group) => group && group.name).slice(0, 2);
};

const getGroupIdByName = (event, groupName) => {
  const groups = Array.isArray(event?.teamGroups) ? event.teamGroups : [];
  const targetKey = normalizeName(groupName);
  const match = groups.find((group) => normalizeName(group.name) === targetKey);
  return match ? match.id : null;
};

const getGroupColorByName = (event, groupName) => {
  const groups = Array.isArray(event?.teamGroups) ? event.teamGroups : [];
  const targetKey = normalizeName(groupName);
  const match = groups.find((group) => normalizeName(group.name) === targetKey);
  return match?.color || null;
};

const getGroupPoints = (event, games, groupAId, groupBId) => {
  let groupAScore = 0;
  let groupBScore = 0;
  const winPoints = Number(event?.gameFormat?.winPoints ?? 1) || 1;
  const drawPointsRaw = event?.gameFormat?.drawPoints;
  const drawPoints =
    drawPointsRaw != null ? Number(drawPointsRaw) : Number((winPoints / 2).toFixed(2));

  (games || []).forEach((game) => {
    if (!game.winningTeamId) {
      const teamAGroupId =
        (event.teams || []).find((team) => team.id === game.teams?.teamA?.id)?.groupId || null;
      const teamBGroupId =
        (event.teams || []).find((team) => team.id === game.teams?.teamB?.id)?.groupId || null;

      if (teamAGroupId === groupAId) {
        groupAScore += drawPoints;
      } else if (teamAGroupId === groupBId) {
        groupBScore += drawPoints;
      }

      if (teamBGroupId === groupAId) {
        groupAScore += drawPoints;
      } else if (teamBGroupId === groupBId) {
        groupBScore += drawPoints;
      }

      return;
    }

    const winningTeam = (event.teams || []).find((team) => team.id === game.winningTeamId);
    const groupId = winningTeam?.groupId || null;

    if (groupId === groupAId) {
      groupAScore += winPoints;
    } else if (groupId === groupBId) {
      groupBScore += winPoints;
    }
  });

  return { groupAScore, groupBScore };
};

const formatPoints = (value) => {
  const numericValue = Number(value || 0);
  if (Number.isInteger(numericValue)) {
    return String(numericValue);
  }
  return numericValue.toFixed(1);
};

const getHeadingBackground = (color, side) => {
  if (!color) {
    return 'transparent';
  }

  if (side === 'left') {
    return `linear-gradient(90deg, ${color} 0%, #ffffff 100%)`;
  }

  return `linear-gradient(270deg, ${color} 0%, #ffffff 100%)`;
};

const PAGE_MAX_WIDTH = 1200;

const GroupMatchup = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { mode, setMode } = useColorMode();

  const [event, setEvent] = useState(null);
  const [linkedEvents, setLinkedEvents] = useState([]);
  const [eventGames, setEventGames] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  useEffect(() => {
    if (!eventId) {
      return;
    }

    setLoading(true);
    const docRef = doc(store, 'events', eventId);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setEvent({ id: docSnap.id, ...docSnap.data() });
      } else {
        setEvent(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [eventId]);

  useEffect(() => {
    if (!event) {
      setLinkedEvents([]);
      return;
    }

    const linkedIds = Array.isArray(event.linkedEvents) ? event.linkedEvents : [];
    if (linkedIds.length === 0) {
      setLinkedEvents([]);
      return;
    }

    let isMounted = true;
    const nextLinkedEvents = [];
    const unsubscribes = linkedIds.map((linkedId) => {
      const docRef = doc(store, 'events', linkedId);
      return onSnapshot(docRef, (docSnap) => {
        if (!isMounted) {
          return;
        }
        if (docSnap.exists()) {
          const existingIndex = nextLinkedEvents.findIndex((item) => item.id === linkedId);
          const nextEvent = { id: docSnap.id, ...docSnap.data() };
          if (existingIndex >= 0) {
            nextLinkedEvents[existingIndex] = nextEvent;
          } else {
            nextLinkedEvents.push(nextEvent);
          }
          setLinkedEvents([...nextLinkedEvents]);
        }
      });
    });

    return () => {
      isMounted = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [event]);

  useEffect(() => {
    if (!event) {
      setEventGames({});
      return;
    }

    const eventsToWatch = [event, ...linkedEvents].filter(Boolean);
    const unsubscribes = eventsToWatch.map((evt) => {
      const collectionRef = collection(store, 'games');
      const q = query(
        collectionRef,
        where('event', '==', evt.id),
        where('status', '==', GameStatus.COMPLETE)
      );

      return onSnapshot(q, (querySnapshot) => {
        const games = liveGameDocs(querySnapshot).map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setEventGames((prev) => ({ ...prev, [evt.id]: games }));
      });
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [event, linkedEvents]);

  const canonicalGroups = useMemo(() => getGroupList(event), [event]);
  const eventsToRender = useMemo(
    () => [event, ...linkedEvents].filter(Boolean),
    [event, linkedEvents]
  );

  const totalPoints = useMemo(() => {
    if (!event || canonicalGroups.length < 2) {
      return { groupAScore: 0, groupBScore: 0 };
    }

    const [groupA, groupB] = canonicalGroups;
    return eventsToRender.reduce(
      (acc, evt) => {
        const groupAId = getGroupIdByName(evt, groupA.name);
        const groupBId = getGroupIdByName(evt, groupB.name);
        const points = getGroupPoints(evt, eventGames[evt.id], groupAId, groupBId);
        return {
          groupAScore: acc.groupAScore + points.groupAScore,
          groupBScore: acc.groupBScore + points.groupBScore
        };
      },
      { groupAScore: 0, groupBScore: 0 }
    );
  }, [canonicalGroups, event, eventGames, eventsToRender]);

  if (loading || !event) {
    return (
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        minHeight="100vh"
        sx={{ width: '100vw', backgroundColor: '#ffffff' }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flexGrow={1}
          width="100%">
          <BSCircularProgress caption="Loading event..." size={40} />
        </Box>
        <Box pb={2}>
          <PoweredBy />
        </Box>
      </Box>
    );
  }

  if (canonicalGroups.length < 2) {
    return (
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        minHeight="100vh"
        sx={{ width: '100vw', backgroundColor: '#ffffff' }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flexGrow={1}
          width="100%">
          <Typography variant="h6">Add two team groups to display this view.</Typography>
        </Box>
        <Box pb={2}>
          <PoweredBy />
        </Box>
      </Box>
    );
  }

  const [groupA, groupB] = canonicalGroups;
  const groupAColor = getGroupColorByName(event, groupA.name);
  const groupBColor = getGroupColorByName(event, groupB.name);

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      sx={{
        width: '100vw',
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        p: { xs: 1.5, sm: 3 }
      }}>
      <Box sx={{ width: '100%', maxWidth: PAGE_MAX_WIDTH }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: { xs: 1.5, sm: 3 },
            mt: { xs: 1, sm: 2 },
            borderRadius: 2,
            backgroundColor: '#ffffff'
          }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: '1fr auto 1fr' }}
            alignItems="center"
            gap={{ xs: 1, sm: 2 }}>
            <Box
              textAlign={{ xs: 'center', sm: 'left' }}
              sx={{
                background: getHeadingBackground(groupAColor, 'left'),
                borderRadius: 1,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1.5 }
              }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {groupA.name}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '2.2rem', sm: '3.4rem' }
                }}>
                <Box component="span" sx={{ color: groupAColor || 'text.primary' }}>
                  {formatPoints(totalPoints.groupAScore)}
                </Box>
                <Box component="span" sx={{ color: 'text.primary' }}>
                  -
                </Box>
                <Box component="span" sx={{ color: groupBColor || 'text.primary' }}>
                  {formatPoints(totalPoints.groupBScore)}
                </Box>
              </Typography>
            </Box>
            <Box
              textAlign={{ xs: 'center', sm: 'right' }}
              sx={{
                background: getHeadingBackground(groupBColor, 'right'),
                borderRadius: 1,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1.5 }
              }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {groupB.name}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            width: '100%',
            mt: { xs: 2, sm: 3 },
            display: 'grid',
            gap: { xs: 1.5, sm: 2 }
          }}>
          {eventsToRender.map((evt) => {
            const groupAId = getGroupIdByName(evt, groupA.name);
            const groupBId = getGroupIdByName(evt, groupB.name);
            const eventGroupAColor = getGroupColorByName(evt, groupA.name) || groupAColor;
            const eventGroupBColor = getGroupColorByName(evt, groupB.name) || groupBColor;
            const groupATeams = (evt.teams || []).filter((team) => team.groupId === groupAId);
            const groupBTeams = (evt.teams || []).filter((team) => team.groupId === groupBId);
            const points = getGroupPoints(evt, eventGames[evt.id], groupAId, groupBId);

            return (
              <Paper
                key={evt.id}
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: 2,
                  backgroundColor: '#fff'
                }}>
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr 1fr', sm: '1fr auto 1fr' }}
                  gridTemplateAreas={{
                    xs: '"teamA teamB" "button button"',
                    sm: '"teamA button teamB"'
                  }}
                  alignItems="center"
                  gap={{ xs: 1.5, sm: 2 }}>
                  <Box textAlign="center" sx={{ gridArea: 'teamA' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: eventGroupAColor || 'inherit',
                        fontSize: { xs: '1.4rem', sm: '1.8rem' }
                      }}>
                      {formatPoints(points.groupAScore)}
                    </Typography>
                    {groupATeams.length > 0 ? (
                      groupATeams.map((team) => (
                        <Typography key={team.id} variant="body2">
                          {team.name}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="caption">No teams in {groupA.name}</Typography>
                    )}
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    minHeight="100%"
                    sx={{ gridArea: 'button' }}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ textTransform: 'none', minWidth: { xs: 220, sm: 'auto' } }}
                      onClick={() => navigate(`/embed/event/${evt.id}?ignorespeciality=true`)}>
                      {evt.name}
                    </Button>
                  </Box>

                  <Box textAlign="center" sx={{ gridArea: 'teamB' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: eventGroupBColor || 'inherit',
                        fontSize: { xs: '1.4rem', sm: '1.8rem' }
                      }}>
                      {formatPoints(points.groupBScore)}
                    </Typography>
                    {groupBTeams.length > 0 ? (
                      groupBTeams.map((team) => (
                        <Typography key={team.id} variant="body2">
                          {team.name}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="caption">No teams in {groupB.name}</Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>

      <Box mt="auto" pt={2}>
        <PoweredBy />
      </Box>
    </Box>
  );
};

export default GroupMatchup;
